/**
 * Info Banner Dialog
 * Équivalent de info_banner.dart dans Flutter
 * Affiche des messages/bannières importantes configurables avec support d'image et d'actions
 */

import { useState, useEffect, useCallback } from 'react'
import { X, Info, ExternalLink, Image as ImageIcon } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { storage } from '@/lib/storage'
import { analyticsService } from '@/services/analytics'

// Banner action button configuration
export interface BannerAction {
  label: string
  navigationUrl?: string
  isPrimary?: boolean
  onPressed?: () => void
}

// Complete banner configuration (matches Flutter BannerConfig)
export interface BannerConfig {
  id: string
  title?: string
  message: string
  imageUrl?: string
  actions?: BannerAction[]
  showOnce?: boolean
  autoDismissSeconds?: number
  isActive?: boolean
}

interface InfoBannerProps {
  banner: BannerConfig
  commercialId?: string
  onDismiss?: () => void
}

export function InfoBanner({ banner, commercialId = '', onDismiss }: InfoBannerProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)
  const [imageError, setImageError] = useState(false)

  useEffect(() => {
    console.debug('🎯 [InfoBanner] Component received banner:', {
      banner,
      commercialId,
      isActive: banner?.isActive
    });

    if (!banner || !banner.isActive) {
      console.debug('🚫 [InfoBanner] Banner not active or missing');
      return
    }

    // Check if banner was already shown for this commercial ID (like Flutter)
    const bannerKey = `${banner.id}_shown_${commercialId}`
    const hasBeenShown = storage.read<boolean>(bannerKey)

    console.debug('🔍 [InfoBanner] Checking if banner was shown before:', {
      bannerKey,
      hasBeenShown,
      showOnce: banner.showOnce
    });

    if (banner.showOnce && hasBeenShown) {
      console.debug(`🚫 [InfoBanner] Banner ${banner.id} already shown for ${commercialId}`);
      return
    }

    // Show banner after a delay (like Flutter's 3 seconds)
    console.debug(`⏰ [InfoBanner] Scheduling banner display in 3 seconds...`);
    const timer = setTimeout(() => {
      console.debug(`✅ [InfoBanner] Showing banner ${banner.id} now!`);
      setIsOpen(true)
      analyticsService.trackEvent('banner_shown', {
        banner_id: banner.id,
        banner_title: banner.title,
        commercial_id: commercialId,
      })

      // Mark as shown if showOnce is true
      if (banner.showOnce) {
        storage.write(bannerKey, true)
        console.debug(`💾 [InfoBanner] Banner ${banner.id} marked as shown for ${commercialId}`)
      }

      // Auto-dismiss if configured (like Flutter)
      if (banner.autoDismissSeconds && banner.autoDismissSeconds > 0) {
        console.debug(`⏰ [InfoBanner] Auto-dismiss in ${banner.autoDismissSeconds} seconds`);
        const dismissTimer = setTimeout(() => {
          handleClose()
        }, banner.autoDismissSeconds * 1000)

        return () => clearTimeout(dismissTimer)
      }
    }, 3000)

    return () => clearTimeout(timer)
  }, [banner, commercialId])

  const handleClose = useCallback(() => {
    setIsOpen(false)
    analyticsService.trackEvent('banner_dismissed', {
      banner_id: banner.id,
      banner_title: banner.title,
      commercial_id: commercialId,
    })
    onDismiss?.()
  }, [banner, commercialId, onDismiss])

  const handleAction = useCallback((action: BannerAction) => {
    if (action.navigationUrl) {
      // Open URL in new tab
      window.open(action.navigationUrl, '_blank')
      analyticsService.trackEvent('banner_action_clicked', {
        banner_id: banner.id,
        action_label: action.label,
        url: action.navigationUrl,
      })
    }

    // Execute custom callback if provided
    if (action.onPressed) {
      action.onPressed()
    }

    // Dismiss dialog after action
    handleClose()
  }, [banner, handleClose])

  // Preload image when component mounts
  useEffect(() => {
    if (banner?.imageUrl) {
      const img = new Image()
      img.onload = () => {
        setImageLoaded(true)
        console.debug(`✅ Banner image loaded: ${banner.imageUrl}`)
      }
      img.onerror = () => {
        setImageError(true)
        console.error(`❌ Failed to load banner image: ${banner.imageUrl}`)
      }
      img.src = banner.imageUrl
    }
  }, [banner?.imageUrl])

  if (!banner || !banner.isActive) {
    return null
  }

  const hasActions = banner.actions && banner.actions.length > 0
  const primaryAction = banner.actions?.find(a => a.isPrimary)
  const secondaryActions = banner.actions?.filter(a => !a.isPrimary) || []

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          />

          {/* Dialog */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
          >
            <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full pointer-events-auto overflow-hidden">
              {/* Top decorative bar with gradient (like Flutter) */}
              <div className="h-1 bg-gradient-to-r from-blue-600 to-purple-600" />

              {/* Image section if available (like Flutter) */}
              {banner.imageUrl && (
                <div className="relative">
                  {imageLoaded && !imageError ? (
                    <div className="relative">
                      <img
                        src={banner.imageUrl}
                        alt={banner.title || 'Banner'}
                        className="w-full h-56 object-cover"
                      />
                      {/* Gradient overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                    </div>
                  ) : imageError ? (
                    <div className="w-full h-56 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                      <div className="text-center">
                        <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                        <p className="text-gray-500 text-sm">Image non disponible</p>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full h-56 bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2" />
                        <p className="text-gray-500 text-sm">Chargement...</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Content section */}
              <div className="p-6">
                {/* Title with icon */}
                {banner.title && (
                  <div className="mb-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-full p-2">
                        <Info className="w-5 h-5 text-white" />
                      </div>
                      <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                        {banner.title}
                      </h2>
                    </div>
                    {/* Decorative underline */}
                    <div className="h-0.5 w-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full" />
                  </div>
                )}

                {/* Message */}
                <p className="text-gray-700 text-base leading-relaxed whitespace-pre-wrap">
                  {banner.message}
                </p>
              </div>

              {/* Actions section (like Flutter) */}
              <div className="px-6 pb-6 space-y-3">
                {hasActions ? (
                  <>
                    {/* Primary action */}
                    {primaryAction && (
                      <button
                        onClick={() => handleAction(primaryAction)}
                        className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3.5 px-4 rounded-xl font-semibold hover:opacity-90 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                      >
                        <span>{primaryAction.label}</span>
                        {primaryAction.navigationUrl && <ExternalLink className="w-4 h-4" />}
                      </button>
                    )}

                    {/* Secondary actions */}
                    {secondaryActions.map((action, index) => (
                      <button
                        key={index}
                        onClick={() => handleAction(action)}
                        className="w-full border-2 border-blue-600 text-blue-600 py-3 px-4 rounded-xl font-semibold hover:bg-blue-50 transition-all flex items-center justify-center gap-2"
                      >
                        <span>{action.label}</span>
                        {action.navigationUrl && <ExternalLink className="w-4 h-4" />}
                      </button>
                    ))}
                  </>
                ) : (
                  // Default action when no custom actions (like Flutter)
                  <button
                    onClick={handleClose}
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3.5 px-4 rounded-xl font-semibold hover:opacity-90 transition-all shadow-lg"
                  >
                    D&apos;accord
                  </button>
                )}
              </div>

              {/* Close button (top-right) */}
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors bg-white/80 hover:bg-white rounded-full p-1"
                aria-label="Fermer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

/**
 * Hook to show banner automatically based on coreController appSettings
 * This is the main integration point like Flutter's _showBannerIfActive()
 */
export function useAutoShowBanner() {
  const [banner, setBanner] = useState<BannerConfig | null>(null)

  const showBanner = (config: BannerConfig) => {
    console.debug('🎯 Showing banner:', config)
    setBanner(config)
  }

  const hideBanner = () => {
    setBanner(null)
  }

  return {
    banner,
    showBanner,
    hideBanner,
  }
}

/**
 * Service to manage banner display (similar to InfoBannerDialog class in Flutter)
 */
export class InfoBannerService {
  /**
   * Show banner if active (like showBannerOnce in Flutter)
   */
  static showBannerIfActive(
    banner: any,
    commercialId: string,
    onShow?: (config: BannerConfig) => void
  ): boolean {
    console.debug('🔍 [InfoBannerService] showBannerIfActive called', {
      banner,
      commercialId,
      hasOnShow: !!onShow
    });

    // Check if banner exists
    if (!banner) {
      console.debug('🚫 [InfoBannerService] No banner data available');
      return false;
    }

    // Check if it's a relation object from Directus (like Flutter's nested data)
    const bannerData = banner.banner_id || banner;

    console.debug('📦 [InfoBannerService] Banner data extracted:', bannerData);

    // Convert banner data to BannerConfig format
    // Note: Directus API returns 'text' field instead of 'message'
    const message = bannerData.message || bannerData.text || '';

    const config: BannerConfig = {
      id: bannerData.id || `banner_${Date.now()}`,
      title: bannerData.title,
      message: message,
      imageUrl: bannerData.imageUrl || bannerData.image,
      actions: bannerData.actions || [],
      showOnce: bannerData.showOnce !== false, // default true
      autoDismissSeconds: bannerData.autoDismissSeconds,
      isActive: bannerData.is_active !== false, // default true
    };

    console.debug('📋 [InfoBannerService] Banner config:', config);

    if (!config.isActive) {
      console.debug('🚫 [InfoBannerService] Banner is not active, is_active:', config.isActive);
      return false;
    }

    if (!config.message && !config.imageUrl) {
      console.debug('🚫 [InfoBannerService] Banner has no content (message or image required)');
      return false;
    }

    console.debug('✅ [InfoBannerService] Banner is ready to display, triggering onShow...');

    // Trigger display
    onShow?.(config);

    return true;
  }

  /**
   * Reset banner display status for testing (like resetBannerForUser in Flutter)
   */
  static resetBannerForUser(bannerId: string, commercialId: string) {
    const bannerKey = `${bannerId}_shown_${commercialId}`
    storage.remove(bannerKey)
    console.debug(`🔄 Reset banner ${bannerId} for ${commercialId}`)
  }

  /**
   * Reset all banner display statuses (like resetAllBanners in Flutter)
   */
  static resetAllBanners() {
    const keys = storage.read<string[]>('storage_keys') || []
    keys.forEach(key => {
      if (key.includes('_shown_')) {
        storage.remove(key)
      }
    })
    console.debug('🔄 Reset all banners')
  }
}
